import { useState } from 'react';

interface Material {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
}

interface MaterialsTrackerProps {
  jobId: string;
  onMaterialsUpdate: (materials: Material[]) => void;
  initialMaterials?: Material[];
}

const MATERIAL_CATEGORIES = [
  'Plumbing',
  'Electrical',
  'Carpentry',
  'Painting',
  'Hardware',
  'Tools',
  'Other',
];

const COMMON_UNITS = ['pcs', 'm', 'kg', 'L', 'box', 'roll', 'bag', 'set'];

export default function MaterialsTracker({
  jobId,
  onMaterialsUpdate,
  initialMaterials = [],
}: MaterialsTrackerProps) {
  const [materials, setMaterials] = useState<Material[]>(initialMaterials);
  const [isAdding, setIsAdding] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    name: '',
    category: 'Other',
    quantity: 1,
    unit: 'pcs',
    unitPrice: 0,
  });

  const handleAddMaterial = () => {
    if (!newMaterial.name || newMaterial.unitPrice <= 0) {
      alert('Please enter material name and valid price');
      return;
    }

    const material: Material = {
      id: Date.now().toString(),
      ...newMaterial,
      totalPrice: newMaterial.quantity * newMaterial.unitPrice,
    };

    const updatedMaterials = [...materials, material];
    setMaterials(updatedMaterials);
    onMaterialsUpdate(updatedMaterials);

    // Save to localStorage
    localStorage.setItem(`job_materials_${jobId}`, JSON.stringify(updatedMaterials));

    // Reset form
    setNewMaterial({
      name: '',
      category: 'Other',
      quantity: 1,
      unit: 'pcs',
      unitPrice: 0,
    });
    setIsAdding(false);
  };

  const handleRemoveMaterial = (id: string) => {
    if (window.confirm('Remove this material?')) {
      const updatedMaterials = materials.filter((m) => m.id !== id);
      setMaterials(updatedMaterials);
      onMaterialsUpdate(updatedMaterials);
      localStorage.setItem(`job_materials_${jobId}`, JSON.stringify(updatedMaterials));
    }
  };

  const totalMaterialsCost = materials.reduce((sum, m) => sum + m.totalPrice, 0);

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Materials Used</h3>
          <p className="text-sm text-gray-500">Track materials for accurate invoicing</p>
        </div>
        <button
          onClick={() => setIsAdding(!isAdding)}
          className="bg-[#ff751f] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#e66a1c] transition-colors whitespace-nowrap flex items-center gap-2"
        >
          <i className={`ri-${isAdding ? 'close' : 'add'}-line text-xl`}></i>
          {isAdding ? 'Cancel' : 'Add Material'}
        </button>
      </div>

      {isAdding && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Material Name *
              </label>
              <input
                type="text"
                value={newMaterial.name}
                onChange={(e) => setNewMaterial({ ...newMaterial, name: e.target.value })}
                placeholder="e.g., Copper Pipe"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff751f] focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={newMaterial.category}
                onChange={(e) => setNewMaterial({ ...newMaterial, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff751f] focus:border-transparent text-sm"
              >
                {MATERIAL_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity *
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={newMaterial.quantity}
                onChange={(e) =>
                  setNewMaterial({ ...newMaterial, quantity: parseFloat(e.target.value) || 0 })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff751f] focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
              <select
                value={newMaterial.unit}
                onChange={(e) => setNewMaterial({ ...newMaterial, unit: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff751f] focus:border-transparent text-sm"
              >
                {COMMON_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit Price (£) *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newMaterial.unitPrice}
                onChange={(e) =>
                  setNewMaterial({ ...newMaterial, unitPrice: parseFloat(e.target.value) || 0 })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff751f] focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Price
              </label>
              <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-semibold text-gray-900">
                £{(newMaterial.quantity * newMaterial.unitPrice).toFixed(2)}
              </div>
            </div>
          </div>

          <button
            onClick={handleAddMaterial}
            className="w-full bg-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors whitespace-nowrap"
          >
            Add Material
          </button>
        </div>
      )}

      {materials.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <i className="ri-tools-line text-4xl mb-2"></i>
          <p className="text-sm">No materials added yet</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 mb-4">
            {materials.map((material) => (
              <div
                key={material.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">{material.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                      {material.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {material.quantity} {material.unit} × £{material.unitPrice.toFixed(2)} = £
                    {material.totalPrice.toFixed(2)}
                  </p>
                </div>
                <button
                  onClick={() => handleRemoveMaterial(material.id)}
                  className="ml-4 text-red-500 hover:text-red-700 transition-colors p-2"
                >
                  <i className="ri-delete-bin-line text-xl"></i>
                </button>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-900">Total Materials Cost</span>
              <span className="text-2xl font-bold text-[#ff751f]">
                £{totalMaterialsCost.toFixed(2)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}